import React, { memo, useEffect, useRef } from 'react'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Bleed, Box, Text, useResponsiveValue } from '@machobear/design-system'
import { useVirtualizer } from '@tanstack/react-virtual'

import { colors } from '@/styles'
import useSessionComplete from '@/hooks/use-session-complete'
import { ChevronLeft, ChevronRight } from '@/components/icons'
import SkeletonCard from '@/components/listing-card-new/SkeletonCard'
import { useTermsDialogContext } from '@/components/terms-dialog/context'

import { ListingCardItem } from './listing-card-item'
import * as styles from './styles.css'
import { useSnapScroll } from './use-snap-scroll'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: ${colors.grey48};
  width: 100%;
`

const ScrollButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  border: 1px solid ${colors.grey88};
  background: ${props => (props.disabled ? colors.grey96 : colors.white)};
  color: ${props => (props.disabled ? colors.grey88 : colors.black)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  transition: all 0.3s ease-in-out;
  &:hover {
    background: ${props => (props.disabled ? colors.grey96 : colors.grey96)};
  }
  svg {
    pointer-events: none;
  }
`

const FeaturedListings = memo(
  ({ listings = [], setHighlightedBuilding, setVisitedBuilding }) => {
    const { t } = useTranslation()
    const hasSession = useSessionComplete()
    const isMobile = useResponsiveValue()({
      desktop: false,
      tablet: false,
      mobile: true
    })

    const { isLoading: isLoadingAgreements } = useTermsDialogContext()
    const showSkeletonState =
      listings.length === 0 || (hasSession && isLoadingAgreements)

    const containerRef = useRef(null)
    const cardWidth = 320
    const gap = isMobile ? 10 : 20

    const virtualizer = useVirtualizer({
      count: listings.length,
      getScrollElement: () => containerRef.current,
      estimateSize: () => cardWidth,
      overscan: 3,
      horizontal: true,
      gap
    })

    useEffect(() => {
      virtualizer.measure()
    }, [isMobile, virtualizer])

    const virtualItems = virtualizer.getVirtualItems().map(item => ({
      ...item,
      item: listings[item.index]
    }))

    const {
      state,
      actions,
      getItemProps,
      handleScroll,
      handleKeyDown,
      handleTouchStart,
      handleTouchEnd
    } = useSnapScroll({
      totalSize: virtualizer.getTotalSize(),
      totalItems: listings.length,
      itemWidth: cardWidth,
      gap,
      scrollToIndex: virtualizer.scrollToIndex,
      containerRef: containerRef
    })

    return (
      <Wrapper>
        <Box display="flex" alignItems="flexEnd" justifyContent="spaceBetween">
          <Text size="small" tone="primary">
            {t('Sponsored')}
          </Text>
          <Box display="flex" alignItems="center" gap="small">
            <ScrollButton
              onClick={actions.scrollPrevious}
              disabled={!state.canScrollPrevious}
              id="city-page-sponsor-listing-back-click"
              title="Scroll Left"
              aria-label="Scroll Left"
            >
              <ChevronLeft />
            </ScrollButton>
            <ScrollButton
              onClick={actions.scrollNext}
              disabled={!state.canScrollNext}
              id="city-page-sponsor-listing-fwd-click"
              title="Scroll Right"
              aria-label="Scroll Right"
            >
              <ChevronRight />
            </ScrollButton>
          </Box>
        </Box>
        {showSkeletonState ? (
          <Box overflow="scroll" height="full">
            <Box
              display="flex"
              style={{
                height: '405px',
                minHeight: '100%',
                paddingRight: '20px',
                gap: isMobile ? '10px' : '20px'
              }}
            >
              {Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} style={{ minWidth: `${cardWidth}px` }}>
                  <SkeletonCard featured />
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Bleed
            right={{
              mobile: 'xsmall',
              tablet: 'medium'
            }}
          >
            <Box
              height="full"
              ref={containerRef}
              className={styles.container}
              onScroll={handleScroll}
              onKeyDown={handleKeyDown}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              tabIndex={0}
            >
              <Box
                aria-label="sponsored-listings"
                data-impression-container="true"
                position="relative"
                style={{
                  height: '405px',
                  minHeight: '100%',
                  width: virtualizer.getTotalSize()
                }}
              >
                {listings.length > 0 ? (
                  virtualItems.map(virtualItem => {
                    const listing = listings[virtualItem.index]
                    const itemProps = getItemProps(virtualItem)
                    return (
                      <Box
                        key={virtualItem.key}
                        style={itemProps.style}
                        height="full"
                      >
                        <ListingCardItem
                          id={listing.listing_id}
                          listing={listing}
                          isFeatured={true}
                          onClick={() => setVisitedBuilding(listing)}
                          onListingHover={() => {
                            setHighlightedBuilding(listing.building_id)
                          }}
                          trackImpression={{
                            id: listing.listing_id,
                            enabled: true,
                            page: 'listings',
                            section: 'listing-card',
                            sponsored: true
                          }}
                        />
                      </Box>
                    )
                  })
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="full"
                    color="grey"
                  >
                    <Text size="small">No featured listings available</Text>
                  </Box>
                )}
              </Box>
            </Box>
          </Bleed>
        )}
      </Wrapper>
    )
  }
)

FeaturedListings.displayName = 'FeaturedListings'

export default FeaturedListings
